/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropType from 'prop-types';
import { Col, Form, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { BaseButton } from 'components/UI/BaseButton';
import { BaseInput } from 'components/UI/BaseInput';
import { fetchMachineList } from 'state/global/machine/operation';
import * as selector from 'state/global/machine/selector';
import { OPERATION_STATUS } from 'util/config/constant';
import { getMachineType } from 'util/machine-type';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { selectGlobalUserInfo } from 'state/global/auth/selector';
import { hasUserInGroup, USER_GROUP_NAME } from 'util/user-group';
import { BaseSelect } from 'components/UI/BaseSelect';
import { OperatorFilter } from '../OperatorFilter';
import { createOperator } from '../state/operation';
import * as folderSelector from '../state/selector';
import { generateResponsibilities } from '../util/generateResponsibilities';
import { OperatorSelectFields } from './OperationSelectFields';

export const OperatorForm = ({ onFilterChange }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.OPERATOR);
  const [collapse, setCollapse] = useState(true);
  const { Option } = BaseSelect;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const responsibilities = useMemo(() => generateResponsibilities(t), [t]);
  const [, onFormFieldsChange] = useState({});

  const globalUserInfo = useSelector((state) => selectGlobalUserInfo(state));
  const { machineListInfo, createOperatorInfo } = useSelector((state) => {
    return {
      machineListInfo: selector.selectMachineListInfo(state),
      createOperatorInfo: folderSelector.selectCreateOperatorInfo(state)
    };
  });

  const isDeveloperUser = useMemo(
    () => hasUserInGroup(globalUserInfo, USER_GROUP_NAME.DEVELOPER),
    [globalUserInfo]
  );

  const countries = useMemo(
    () => [
      { id: '+31', label: 'NL', altName: 'Netherlands', icon: 'netherlands' },
      { id: '+32', label: 'BE', altName: 'Belgium', icon: 'belgium' },
      { id: '+33', label: 'FR', altName: 'France', icon: 'france' },
      { id: '+352', label: 'LU', altName: 'Luxemburg', icon: 'luxembourg' },
      { id: '+353', label: 'IE', altName: 'Ireland', icon: 'ireland' },
      { id: '+358', label: 'FI', altName: 'Finland', icon: 'finland' },
      { id: '+39', label: 'IT', altName: 'Italy', icon: 'italy' },
      { id: '+41', label: 'CH', altName: 'Switzerland', icon: 'switzerland' },
      {
        id: '+420',
        label: 'CZ',
        altName: 'Czech Republic',
        icon: 'czech-republic'
      },
      { id: '+43', label: 'AT', altName: 'Austria', icon: 'austria' },
      {
        id: '+44',
        label: 'GB',
        altName: 'Great Britain',
        icon: 'united-kingdom'
      },
      { id: '+45', label: 'DK', altName: 'Denmark', icon: 'denmark' },
      { id: '+46', label: 'SE', altName: 'Sweden', icon: 'sweden' },
      { id: '+47', label: 'NO', altName: 'Norway', icon: 'norway' },
      { id: '+48', label: 'PL', altName: 'Poland', icon: 'poland' },
      { id: '+49', label: 'DE', altName: 'Germany', icon: 'germany' },
      { id: '+61', label: 'AU', altName: 'Australia', icon: 'australia' },
      { id: '+64', label: 'NZ', altName: 'New Zealand', icon: 'new-zealand' },
      { id: '+65', label: 'SGP', altName: 'Singapore', icon: 'singapore' },
      { id: '+81', label: 'JP', altName: 'Japan', icon: 'japan' },
      { id: '+421', label: 'SK', altName: 'Slovakia', icon: 'slovakia' },
      { id: '+1', label: 'US', altName: 'USA', icon: 'usa' }
    ],
    []
  );

  const countryOptions = useMemo(() => {
    // Vietnam option is used for testing purpose
    return isDeveloperUser
      ? countries.concat({
          id: '+84',
          label: 'VN',
          altName: 'Vietnam',
          icon: 'vietnam'
        })
      : countries;
  }, [countries, isDeveloperUser]);

  useEffect(() => {
    dispatch(fetchMachineList({ limit: 100 }));
  }, [dispatch]);

  const normalizeFormData = useCallback(
    (values) => {
      const { phoneNumberPrefix, phoneNumberSuffix, ...rest } = values;
      const phoneNumber =
        phoneNumberPrefix && phoneNumberSuffix
          ? `${phoneNumberPrefix}.${phoneNumberSuffix}`
          : undefined;

      const formValues = {
        ...rest,
        isEmailNotified: !!values.email,
        isPhoneNotified: !!values.phoneNumber,
        phoneNumber
      };

      // Convert undefined to false
      responsibilities.forEach((responsibility) => {
        const fieldName = responsibility.name;
        if (fieldName) {
          formValues[fieldName] = !!values[fieldName];
        }
      });

      return formValues;
    },
    [responsibilities]
  );

  useEffect(() => {
    if (createOperatorInfo.status === OPERATION_STATUS.SUCCESS) {
      form.resetFields();
      setCollapse(true);
    }
  }, [createOperatorInfo.status, form]);

  const onFinish = useCallback(
    (values) => {
      const formValues = normalizeFormData(values);
      dispatch(createOperator(formValues));
    },
    [dispatch, normalizeFormData]
  );

  const handleChange = useCallback(
    (newFilters) => {
      onFilterChange(newFilters);
    },
    [onFilterChange]
  );

  const emailValidator = useCallback(
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (
          !!value ||
          !!(
            getFieldValue('phoneNumberSuffix') &&
            getFieldValue('phoneNumberPrefix')
          )
        ) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error(t('operatorForm.errors.emailOrPhoneNumberRequired'))
        );
      }
    }),
    [t]
  );

  const phoneNumberValidator = useCallback(
    ({ getFieldValue }) => ({
      validator() {
        if (
          !!getFieldValue('email') ||
          !!(
            getFieldValue('phoneNumberSuffix') &&
            getFieldValue('phoneNumberPrefix')
          )
        ) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error(t('operatorForm.errors.emailOrPhoneNumberRequired'))
        );
      }
    }),
    [t]
  );

  return (
    <>
      <Row
        gutter={[12, 12]}
        justify="space-between"
        align="bottom"
        className="mb-8">
        <Col>
          <OperatorFilter onChange={handleChange} />
        </Col>
        <Col>
          <BaseButton
            className={`pr-2 ${!collapse && 'active'} add-operator-btn`}
            onClick={() => setCollapse((prevCollapse) => !prevCollapse)}>
            {collapse ? t('addOperatorButton') : t('discardChangesButton')}
            {collapse && <PlusOutlined />}
          </BaseButton>
        </Col>
      </Row>
      {!collapse && (
        <Form
          className="operator-form"
          initialValues={{
            language: 'en'
          }}
          form={form}
          onFieldsChange={() => onFormFieldsChange({})}
          onFinish={onFinish}>
          <Row justify="space-between">
            <Col md={5} lg={5} span={10}>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: t('operatorForm.errors.nameRequired')
                  }
                ]}>
                <BaseInput label={t('operatorForm.fields.name')} />
              </Form.Item>
            </Col>

            <Col md={5} lg={5} span={10}>
              <Form.Item
                name="email"
                dependencies={['phoneNumberPrefix', 'phoneNumberSuffix']}
                rules={[
                  emailValidator,
                  {
                    type: 'email',
                    message: t('operatorForm.errors.emailFormat')
                  }
                ]}>
                <BaseInput label={t('operatorForm.fields.email')} />
              </Form.Item>
            </Col>

            <Col md={5} lg={5} span={10} className="phone-number-wrapper">
              <span className="base-input-label">
                {t('operatorForm.fields.phoneNumber')}
              </span>
              <div className="input-group">
                <Form.Item
                  name="phoneNumberPrefix"
                  dependencies={['email']}
                  rules={[phoneNumberValidator]}>
                  <BaseSelect
                    className="custom-select"
                    onChange={() => form.validateFields(['phoneNumberSuffix'])}>
                    {countryOptions.map((country) => (
                      <Option value={country.id} label={country.label}>
                        <div>
                          <img
                            src={`/assets/images/icons/country/${country.icon}.svg`}
                            alt={country.altName}
                            width="16"
                          />
                          &nbsp;
                          <span className="d-inline-block min-w-20">
                            {country.label}
                          </span>
                          &nbsp;
                          <span className="d-inline-block min-w-40">
                            {country.id}
                          </span>
                          &ndash;
                        </div>
                      </Option>
                    ))}
                  </BaseSelect>
                </Form.Item>

                <Form.Item
                  name="phoneNumberSuffix"
                  dependencies={['email']}
                  rules={[phoneNumberValidator]}>
                  <BaseInput
                    onChange={() => form.validateFields(['phoneNumberPrefix'])}
                    type="tel"
                    pattern="[0-9]+"
                  />
                </Form.Item>
              </div>

              <p className="phone-number-error ant-form-item-explain">
                {form.getFieldError('phoneNumberSuffix') ||
                  form.getFieldError('phoneNumberPrefix')}
              </p>
            </Col>

            <Col md={5} lg={5} span={10}>
              <>
                <span className="base-input-label">
                  {t('operatorForm.fields.assignMachine')}
                </span>
                <Form.Item
                  name="machineId"
                  rules={[
                    {
                      required: true,
                      message: t('operatorForm.errors.assignMachineRequired')
                    }
                  ]}>
                  <BaseSelect className="custom-select">
                    {machineListInfo?.data?.map((machine) => (
                      <Option key={machine.id} value={machine.id}>
                        {getMachineType(machine.materialNumber)} / &nbsp;
                        {machine.alias}
                      </Option>
                    ))}
                  </BaseSelect>
                </Form.Item>
              </>
            </Col>
            <Col md={2} lg={2} span={10}>
              <></>
            </Col>
          </Row>
          <OperatorSelectFields
            responsibilities={responsibilities}
            isLoading={createOperatorInfo.isLoading}
            setFieldsValue={form.setFieldsValue}
          />
        </Form>
      )}
    </>
  );
};

OperatorForm.propTypes = {
  onFilterChange: PropType.func.isRequired
};
